import type { SkillView } from "./types";

/**
 * What the items in use add to a skill, as the skills panel says it
 * (2026-10-08: "when skills are modified by items, show the adjustment in
 * the skills panel"). The level and its bar are what was earned; this is
 * written beside the level, in a colour of its own, and the hover says
 * which piece adds what. The figures are the server's (SkillView.boost and
 * boosts), the same ones a spell is cast with, so nothing is added up here
 * that the server did not.
 */

/** A level's worth of adjustment with its sign: "+5", and a true minus
 *  sign, "−2", for a piece that ever takes levels away. Empty for none. */
export function boostText(n: number | undefined): string {
  if (!n) return "";
  return n < 0 ? `−${-n}` : `+${n}`;
}

/** The sentence about the items, or empty when none moves the skill:
 *  "+5 from the One Ring and +5 from the geomancer's staff: magic 22 while
 *  they are in use, 144% spell damage." */
export function boostSentence(s: SkillView): string {
  if (!s.boost || !s.boosts?.length) return "";
  const parts = s.boosts.map((b) => `${boostText(b.levels)} from ${b.from}`);
  const list = parts.length < 2 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  const they = s.boosts.length < 2 ? "it is" : "they are";
  const at = Math.max(0, s.level + s.boost);
  return `${list}: ${s.name} ${at} while ${they} in use${s.boosteffect ? `, ${s.boosteffect}` : ""}.`;
}

/** A skill row's hover: what the level earned does, how far to the next,
 *  and what the items add. */
export function skillHover(s: SkillView): string {
  const earned = `${s.name} ${s.level}: ${s.effect}. ${s.pct}% of the way to level ${s.level + 1}.`;
  const items = boostSentence(s);
  return items ? `${earned} ${items}` : earned;
}
